import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Card, 
  CardContent, 
  Grid,
  Stack,
  Avatar,
  useTheme
} from '@mui/material';
import { motion, type Variants } from 'motion/react';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PeopleIcon from '@mui/icons-material/People';
import StarIcon from '@mui/icons-material/Star';
import PhoneIcon from '@mui/icons-material/Phone';
import WorkIcon from '@mui/icons-material/Work';
import BuildIcon from '@mui/icons-material/Build';

const featureList = [
  { 
    title: 'Appointment Scheduling', 
    description: 'Get more appointments on autopilot with an easy-to-use scheduling system',
    icon: <CalendarMonthIcon fontSize="large" />
  },
  { 
    title: 'Customer Management', 
    description: 'Get more customers and keep them coming back with powerful CRM tools',
    icon: <PeopleIcon fontSize="large" />
  },
  { 
    title: 'Service Tracking', 
    description: 'Manage vehicle history, track service records, and schedule maintenance reminders',
    icon: <BuildIcon fontSize="large" />
  },
  { 
    title: 'Reviews & Reputation', 
    description: "Get more reviews automatically and build your shop's online reputation",
    icon: <StarIcon fontSize="large" />
  },
  { 
    title: 'Customer Communication', 
    description: 'Stay connected with customers through automated reminders and notifications',
    icon: <PhoneIcon fontSize="large" />
  },
  { 
    title: 'Repair Shop Analytics', 
    description: 'Track your business growth with comprehensive reporting and analytics',
    icon: <WorkIcon fontSize="large" />
  },
];

const testimonials = [
  {
    name: "Mike Collins",
    title: "Owner, Collins Auto Care",
    quote: "We went from getting maybe one to two online appointments a week to suddenly... in the first week, we were getting ten a day. It was insane."
  },
  {
    name: "Bill Bock",
    title: "Owner, Bock Automotive",
    quote: "MechaniX is an absolute game-changer for our business! From its intuitive interface to its robust features, this system has revolutionized how we manage our customer relationships."
  },
  {
    name: "Greg Buckley",
    title: "Owner, Buckley's Auto Care",
    quote: "MechaniX is one of hardest working solutions out there that actually does what it says it will do. We're getting way more than we expected."
  }
];

const MarketingPage: React.FC = () => {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const isDarkMode = theme.palette.mode === 'dark';

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box 
        sx={{ 
          background: isDarkMode 
            ? 'linear-gradient(135deg, #1E1E1E 0%, #292929 100%)' 
            : `linear-gradient(135deg, #f5f9ff 0%, #e8f0ff 100%)`,
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid component="div" sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                <motion.div variants={itemVariants}>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 700,
                      mb: 2,
                      fontSize: { xs: '2.2rem', md: '3.5rem' },
                      lineHeight: 1.2,
                      background: isDarkMode 
                        ? 'linear-gradient(45deg, #fff 30%, #e0e0e0 90%)' 
                        : `linear-gradient(45deg, #3a86ff 30%, #2563eb 90%)`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    One platform.<br />
                    Real results.
                  </Typography>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Typography
                    variant="h5"
                    sx={{
                      mb: 4,
                      color: isDarkMode ? 'rgba(255,255,255,0.8)' : 'text.secondary',
                      fontWeight: 400,
                      maxWidth: 500
                    }}
                  >
                    Everything you need, nothing you don't. A purpose-built CRM for auto repair shops.
                  </Typography>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    sx={{
                      px: 5,
                      py: 1.5,
                      borderRadius: 2,
                      boxShadow: `0 0 20px ${isDarkMode ? 'rgba(76,208,208,0.3)' : 'rgba(58,134,255,0.3)'}`,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      '&:hover': {
                        boxShadow: `0 0 30px ${isDarkMode ? 'rgba(76,208,208,0.5)' : 'rgba(58,134,255,0.5)'}`,
                        transform: 'translateY(-2px)',
                        transition: 'all 0.3s ease'
                      }
                    }}
                    href="#/shops"
                  >
                    Book a Demo
                  </Button>
                </motion.div>
              </motion.div>
            </Grid>
            
            <Grid component="div" sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
              >
                <Box
                  component="img"
                  src="/images/dashboard-screenshot.png"
                  alt="MechaniX Dashboard"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: 4,
                    boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
                    transform: 'perspective(800px) rotateY(-5deg) rotateX(5deg) rotate(1deg)',
                  }}
                />
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Typography 
          variant="h3" 
          textAlign="center" 
          fontWeight="bold" 
          mb={2}
          sx={{
            background: isDarkMode 
              ? 'linear-gradient(45deg, #fff 30%, #e0e0e0 90%)' 
              : `linear-gradient(45deg, #3a86ff 30%, #2563eb 90%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          MechaniX is 6+ products, all-in-one
        </Typography>
        <Typography 
          variant="h6" 
          textAlign="center" 
          mb={8} 
          color="text.secondary"
        >
          Consolidate tools, cut costs, grow your business
        </Typography>

        <Grid container spacing={4}>
          {featureList.map((feature, index) => (
            <Grid component="div" sx={{ gridColumn: { xs: 'span 12', sm: 'span 6', md: 'span 4' } }} key={index}>
              <motion.div
                whileHover={{ 
                  y: -10,
                  boxShadow: '0 10px 40px rgba(0,0,0,0.1)' 
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Card 
                  sx={{ 
                    height: '100%', 
                    borderRadius: 4,
                    p: 1,
                    border: isDarkMode ? '1px solid rgba(255,255,255,0.05)' : 'none',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <CardContent>
                    <Box sx={{ color: primaryColor, mb: 2 }}>
                      {feature.icon}
                    </Box>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                      {feature.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* How it works section */}
      <Box sx={{ 
        py: 10, 
        background: isDarkMode 
          ? 'linear-gradient(135deg, #292929 0%, #1E1E1E 100%)' 
          : `linear-gradient(135deg, #f0f7ff 0%, #e6f0ff 100%)`
      }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            textAlign="center" 
            fontWeight="bold" 
            mb={2}
            sx={{
              background: isDarkMode 
                ? 'linear-gradient(45deg, #fff 30%, #e0e0e0 90%)' 
                : `linear-gradient(45deg, #3a86ff 30%, #2563eb 90%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            How shops grow with MechaniX
          </Typography>
          <Typography 
            variant="h6" 
            textAlign="center" 
            mb={8} 
            color="text.secondary"
          >
            It's as easy as 1, 2, 3!
          </Typography>

          <Grid container spacing={5}>
            <Grid component="div" sx={{ gridColumn: { xs: 'span 12', md: 'span 4' } }}>
              <motion.div
                whileHover={{ scale: 1.03 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Card 
                  sx={{ 
                    height: '100%', 
                    p: 3, 
                    borderRadius: 4,
                    textAlign: 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '2rem',
                      fontWeight: 'bold',
                      margin: '0 auto 24px'
                    }}
                  >
                    1
                  </Box>
                  <Typography variant="h5" gutterBottom fontWeight="bold">
                    Acquisition
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Get more customers online with MechaniX's booking tool & appointment scheduling
                  </Typography>
                </Card>
              </motion.div>
            </Grid>

            <Grid component="div" sx={{ gridColumn: { xs: 'span 12', md: 'span 4' } }}>
              <motion.div
                whileHover={{ scale: 1.03 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Card 
                  sx={{ 
                    height: '100%', 
                    p: 3, 
                    borderRadius: 4,
                    textAlign: 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '2rem',
                      fontWeight: 'bold',
                      margin: '0 auto 24px'
                    }}
                  >
                    2
                  </Box>
                  <Typography variant="h5" gutterBottom fontWeight="bold">
                    Communication
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Communicate with customers 24/7 with automated messaging and notifications
                  </Typography>
                </Card>
              </motion.div>
            </Grid>

            <Grid component="div" sx={{ gridColumn: { xs: 'span 12', md: 'span 4' } }}>
              <motion.div
                whileHover={{ scale: 1.03 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <Card 
                  sx={{ 
                    height: '100%', 
                    p: 3, 
                    borderRadius: 4,
                    textAlign: 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '2rem',
                      fontWeight: 'bold',
                      margin: '0 auto 24px'
                    }}
                  >
                    3
                  </Box>
                  <Typography variant="h5" gutterBottom fontWeight="bold">
                    Retention
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Keep customers coming back with service reminders and loyalty features
                  </Typography>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Testimonials */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Typography 
          variant="h3" 
          textAlign="center" 
          fontWeight="bold" 
          mb={8}
          sx={{
            background: isDarkMode 
              ? 'linear-gradient(45deg, #fff 30%, #e0e0e0 90%)' 
              : `linear-gradient(45deg, #3a86ff 30%, #2563eb 90%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Shop owners love MechaniX
        </Typography>

        <Grid container spacing={4}>
          {testimonials.map((testimonial, index) => (
            <Grid component="div" sx={{ gridColumn: { xs: 'span 12', md: 'span 4' } }} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
              >
                <Card 
                  sx={{ 
                    height: '100%', 
                    p: 3, 
                    borderRadius: 4,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box sx={{ mb: 3, display: 'flex' }}>
                    {[...Array(5)].map((_, i) => (
                      <StarIcon key={i} sx={{ color: '#FFD700', mr: 0.5 }} />
                    ))}
                  </Box>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontStyle: 'italic', 
                      mb: 3,
                      flex: 1,
                      fontSize: '1.1rem',
                    }}
                  >
                    "{testimonial.quote}"
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: primaryColor,
                        width: 50,
                        height: 50,
                        mr: 2
                      }}
                    >
                      {testimonial.name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {testimonial.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {testimonial.title}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Call-to-Action Section */}
      <Box sx={{ 
        py: 10, 
        background: isDarkMode 
          ? 'linear-gradient(135deg, #1E1E1E 0%, #292929 100%)' 
          : 'linear-gradient(135deg, #3a86ff 0%, #2563eb 100%)'
      }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Typography 
              variant="h3" 
              gutterBottom 
              fontWeight="bold"
              color={isDarkMode ? "white" : "white"}
            >
              Ready to grow your auto repair business?
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ mb: 6, opacity: 0.9 }}
              color={isDarkMode ? "white" : "white"}
            >
              Join 2,000+ shops who trust MechaniX to streamline their operations
            </Typography>
            <Stack 
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
            >
              <Button
                variant="contained"
                size="large"
                sx={{
                  px: 5,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  backgroundColor: 'white',
                  color: isDarkMode ? '#4CD0D0' : '#3a86ff',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.9)',
                  }
                }}
                href="#/shops"
              >
                Book a Demo
              </Button>
              <Button
                variant="outlined"
                size="large"
                sx={{
                  px: 5,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderColor: 'white',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  }
                }}
                href="#/login"
              >
                Sign In
              </Button>
            </Stack>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default MarketingPage; 